import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/utils/shadcn';

const buttonVariants = cva(
    'focus-visible:ring-primary-600 shadow-xs focus-visible:outline-hidden inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors hover:shadow-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
    {
        variants: {
            variant: {
                default:
                    'border-primary-700/60 bg-primary-600 hover:bg-primary-500 active:bg-primary-700 border text-gray-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90',
                destructive:
                    'border border-red-600 bg-red-600 text-gray-50 hover:bg-red-500 active:bg-red-700 dark:bg-red-900 dark:text-gray-50 dark:hover:bg-red-900/90',
                warning:
                    'border border-orange-600 bg-orange-500 text-gray-50 hover:bg-orange-600 dark:bg-orange-900 dark:text-gray-50 dark:hover:bg-orange-900/90',
                destructiveLight:
                    'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100/90 focus-visible:ring-red-200 dark:bg-red-900 dark:text-gray-50 dark:hover:bg-red-900/90',
                main: 'border-primary-800 bg-primary-700 hover:bg-primary-800 border text-white dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50 px-6',
                sm: 'border-gray-300 bg-white text-gray-800 hover:bg-gray-100 hover:text-gray-900 dark:border-gray-800 border dark:bg-gray-950 dark:hover:bg-gray-800 px-6',
                outline:
                    'border border-gray-200 bg-white hover:bg-gray-100 hover:fill-gray-900 hover:text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50',
                dropdown:
                    'group relative justify-start truncate border border-gray-200 bg-white text-base font-normal hover:bg-gray-100 hover:text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive',
                secondary:
                    'bg-gray-100 text-gray-900 hover:bg-gray-200/80 dark:bg-gray-800 dark:text-gray-50 dark:hover:bg-gray-800/80',
                ghost: 'shadow-none hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-50',
                link: 'font-normal text-gray-900 underline-offset-4 shadow-none hover:underline dark:text-gray-50',
                activeGray: 'w-full bg-gray-100 font-normal text-gray-900 shadow-none dark:bg-gray-800',
                'outline-solid':
                    'border border-gray-200 bg-white hover:bg-gray-100 hover:fill-gray-900 hover:text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 px-3',
                lg: 'h-11 px-8',
                icon: 'size-10',
                // These are used for dynamic sizing so that buttons with icons become just icons and no text on mobile
                lgIcon: 'size-10 shrink-0 lg:w-auto lg:px-4 lg:py-2',
                mdIcon: 'size-10 shrink-0 md:w-auto md:px-4 md:py-2',
                smIcon: 'size-10 shrink-0 sm:w-auto sm:px-4 sm:py-2',
                xsIcon: 'xs:w-auto xs:px-4 xs:py-2 size-10 shrink-0',
                xsText: 'xs:px-4 w-auto p-2',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

function Button({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot : 'button';

    return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
